import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h3>{`Description`}</h3>
    <p>{`Commands like `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` and the dependency sections in the
`}<inlineCode parentName="p">{`package.json`}</inlineCode>{` use a package name specifier.  This can be many different
things that all refer to a "package".  Examples include a package name,
git url, tarball, or local directory.  These will generally be referred
to as `}<inlineCode parentName="p">{`<package-spec>`}</inlineCode>{` in the help output for the npm commands that use
this package name specifier.`}</p>
    <h3>{`Package name`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`[<@scope>/]<pkg>`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`[<@scope>/]<pkg>@<tag>`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`[<@scope>/]<pkg>@<version>`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`[<@scope>/]<pkg>@<version range>`}</inlineCode></li>
    </ul>
    <p>{`Refers to a package by name, with or without a scope, and optionally
tag, version, or version range.  This is typically used in combination
with the `}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/config#registry"
      }}>{`registry`}</a>{` config to refer to a
package in a registry.`}</p>
    <p>{`Examples:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`npm`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`@npmcli/arborist`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`@npmcli/arborist@latest`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`npm@6.13.1`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`npm@^4.0.0`}</inlineCode></li>
    </ul>
    <h3>{`Aliases`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`<alias>@npm:<name>`}</inlineCode></li>
    </ul>
    <p>{`Primarily used by commands like `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` and in the dependency
sections in the `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`, this refers to a package by an alias.
The `}<inlineCode parentName="p">{`<alias>`}</inlineCode>{` is the name of the package as it is reified in the
`}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder, and the `}<inlineCode parentName="p">{`<name>`}</inlineCode>{` refers to a package name as
found in the configured registry.`}</p>
    <p>{`See `}<inlineCode parentName="p">{`Package name`}</inlineCode>{` above for more info on referring to a package by
name, and `}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/config#registry"
      }}>{`registry`}</a>{` for configuring which
registry is used when referring to a package by name.`}</p>
    <p>{`Examples:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`semver:@npm:@npmcli/semver-with-patch`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`semver:@npm:semver@7.2.2`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`semver:@npm:semver@legacy`}</inlineCode></li>
    </ul>
    <h3>{`Folders`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`<folder>`}</inlineCode></li>
    </ul>
    <p>{`This refers to a package on the local filesystem.  Specifically this is
a folder with a `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file in it.  This `}<em parentName="p">{`should`}</em>{` always be
prefixed with a `}<inlineCode parentName="p">{`/`}</inlineCode>{` or `}<inlineCode parentName="p">{`./`}</inlineCode>{` (or your OS equivalent) to reduce confusion.
npm currently will parse a string with more than one `}<inlineCode parentName="p">{`/`}</inlineCode>{` in it as a
folder, but this is legacy behavior that may be removed in a future
version.`}</p>
    <p>{`Examples:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`./my-package`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`/opt/npm/my-package`}</inlineCode></li>
    </ul>
    <h3>{`Tarballs`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`<tarball file>`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`<tarball url>`}</inlineCode></li>
    </ul>
    <p>{`Examples:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`./my-package.tgz`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`https://registry.npmjs.org/semver/-/semver-1.0.0.tgz`}</inlineCode></li>
    </ul>
    <p>{`Refers to a package in a tarball format, either on the local filesystem
or remotely via url.  This is the format that packages exist in when
uploaded to a registry.`}</p>
    <h3>{`git urls`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`<git:// url>`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`<github username>/<github project>`}</inlineCode></li>
    </ul>
    <p>{`Refers to a package in a git repo.  This can be a full git url, git
shorthand, or a username/package on GitHub.  You can specify a
git tag, branch, or other git ref by appending `}<inlineCode parentName="p">{`#ref`}</inlineCode>{`.`}</p>
    <p>{`Examples:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`https://github.com/npm/cli.git`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`git@github.com:npm/cli.git`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`git+ssh://git@github.com/npm/cli#v6.0.0`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`github:npm/cli#HEAD`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`npm/cli#c12ea07`}</inlineCode></li>
    </ul>
    <h3>{`See also`}</h3>
    <p><a parentName="p" {...{
        "href": "https://npm.im/npm-package-arg"
      }}>{`npm-package-arg`}</a>{`
`}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/scope"
      }}>{`scope`}</a>{`
`}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/config"
      }}>{`config`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      